import React, { useState } from "react";
import {
  TransformWrapper,
  TransformComponent,
  useControls,
} from "react-zoom-pan-pinch";
import {
  FileText,
  LogOut,
  Loader2,
  FileQuestion,
  AlertCircle,
  ExternalLink,
  ImageOff,
  MousePointer2,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Settings,
  ChevronRight,
  ChevronLeft,
  FileSpreadsheet,
  X,
} from "lucide-react";
import { ChatPanel } from "./ChatPanel";

interface RunInfo {
  runId: string;
  runName?: string;
}

interface CellReference {
  cell: string;
  sheet: string;
  value: string | number | null;
}

interface SourceImage {
  file_name: string;
  page_number: number;
  image_url: string;
  value?: number | string;
  formula?: string;
}

interface ReferenceViewerProps {
  runInfo: RunInfo;
  selectedCell: CellReference | null;
  sourceData: {
    cell_id?: string;
    images?: SourceImage[];
    notFound?: boolean;
    error?: boolean;
  } | null;
  loading: boolean;
  loadingData?: boolean;
  dataLoaded?: boolean;
  onRefresh: () => void;
  onSignOut?: () => void;
  userEmail?: string;
  summaryData: any;
  loadingSummary: boolean;
}

// Zoom controls component
function ZoomControls() {
  const { zoomIn, zoomOut, resetTransform } = useControls();

  return (
    <div className="absolute bottom-3 right-3 flex items-center gap-1 bg-white/90 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200 p-1">
      <button
        onClick={() => zoomIn()}
        className="p-1.5 hover:bg-gray-100 rounded transition-colors"
        title="Zoom in"
      >
        <ZoomIn className="w-4 h-4 text-gray-600" />
      </button>
      <button
        onClick={() => zoomOut()}
        className="p-1.5 hover:bg-gray-100 rounded transition-colors"
        title="Zoom out"
      >
        <ZoomOut className="w-4 h-4 text-gray-600" />
      </button>
      <div className="w-px h-4 bg-gray-200" />
      <button
        onClick={() => resetTransform()}
        className="p-1.5 hover:bg-gray-100 rounded transition-colors"
        title="Reset zoom"
      >
        <RotateCcw className="w-4 h-4 text-gray-600" />
      </button>
    </div>
  );
}

// Source card component with zoom/pan
function SourceCard({ image }: { image: SourceImage }) {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      {/* Card Header */}
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
          <FileText className="w-4 h-4 text-purple-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {image.file_name}
          </p>
          <p className="text-xs text-gray-400">Page {image.page_number}</p>
        </div>
        <button
          onClick={() => window.open(image.image_url, "_blank")}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
          title="Open in new tab"
        >
          <ExternalLink className="w-4 h-4" />
        </button>
      </div>

      {/* Value and Formula */}
      {(image.value !== undefined || image.formula) && (
        <div className="px-4 pb-3 space-y-2">
          {image.value !== undefined && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500">Value:</span>
              <span className="text-sm font-semibold text-gray-900 bg-purple-50 px-2 py-0.5 rounded">
                {image.value}
              </span>
            </div>
          )}
          {image.formula && (
            <div className="flex items-start gap-2">
              <span className="text-xs font-medium text-gray-500 mt-0.5">Formula:</span>
              <code className="text-xs font-mono text-purple-700 bg-purple-50 px-2 py-1 rounded break-all">
                {image.formula}
              </code>
            </div>
          )}
        </div>
      )}

      {/* Image Preview with Zoom/Pan */}
      <div className="px-4 pb-4">
        <div className="bg-gray-50 rounded-lg overflow-hidden relative">
          {imageError ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <ImageOff className="w-8 h-8 mb-3 opacity-40" />
              <p className="text-sm">Preview not available</p>
              <button
                onClick={() => window.open(image.image_url, "_blank")}
                className="mt-3 text-xs text-purple-600 hover:text-purple-700 font-medium"
              >
                Open directly
              </button>
            </div>
          ) : (
            <>
              {!imageLoaded && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-300" />
                </div>
              )}
              <div className={imageLoaded ? "" : "hidden"}>
                <TransformWrapper
                  initialScale={1}
                  minScale={0.5}
                  maxScale={5}
                  centerOnInit
                  wheel={{ step: 0.1 }}
                  doubleClick={{ mode: "zoomIn" }}
                >
                  <div className="relative" style={{ height: "350px" }}>
                    <TransformComponent
                      wrapperStyle={{
                        width: "100%",
                        height: "100%",
                      }}
                      contentStyle={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={image.image_url}
                        alt={`Preview of ${image.file_name}`}
                        className="max-w-full max-h-full object-contain"
                        onLoad={() => setImageLoaded(true)}
                        onError={() => setImageError(true)}
                      />
                    </TransformComponent>
                    <ZoomControls />
                  </div>
                </TransformWrapper>
              </div>

              {/* Help text */}
              {imageLoaded && (
                <div className="px-3 py-2 bg-gray-100/50 text-center">
                  <p className="text-[10px] text-gray-400">
                    Scroll to zoom • Drag to pan • Double-click to zoom in
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Helper to get file type icon color
function getFileIconColor(fileName: string): string {
  const ext = fileName.split(".").pop()?.toLowerCase();
  if (ext === "pdf") return "text-red-500 bg-red-50";
  if (ext === "xlsx" || ext === "xls" || ext === "csv") return "text-green-600 bg-green-50";
  return "text-blue-500 bg-blue-50";
}

// Helper to get file icon component
function FileIcon({ fileName }: { fileName: string }) {
  const ext = fileName.split(".").pop()?.toLowerCase();
  const colorClass = getFileIconColor(fileName);
  const [textColor, bgColor] = colorClass.split(" ");

  if (ext === "xlsx" || ext === "xls" || ext === "csv") {
    return (
      <div className={`w-8 h-8 rounded-lg ${bgColor} flex items-center justify-center flex-shrink-0`}>
        <FileSpreadsheet className={`w-4 h-4 ${textColor}`} />
      </div>
    );
  }
  return (
    <div className={`w-8 h-8 rounded-lg ${bgColor} flex items-center justify-center flex-shrink-0`}>
      <FileText className={`w-4 h-4 ${textColor}`} />
    </div>
  );
}

// Document page viewer overlay
function DocumentViewer({
  document,
  onClose,
}: {
  document: { file_name: string; pages: { page_number: number; image_url: string }[] };
  onClose: () => void;
}) {
  const [currentPage, setCurrentPage] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const pages = document.pages || [];
  const page = pages[currentPage];

  const goNext = () => {
    if (currentPage < pages.length - 1) {
      setCurrentPage(currentPage + 1);
      setImageLoaded(false);
      setImageError(false);
    }
  };

  const goPrev = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
      setImageLoaded(false);
      setImageError(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Viewer header */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100 flex-shrink-0">
        <button
          onClick={onClose}
          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          title="Back to list"
        >
          <ChevronLeft className="w-4 h-4 text-gray-600" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{document.file_name}</p>
          <p className="text-xs text-gray-400">
            Page {page?.page_number ?? "–"} · {currentPage + 1} of {pages.length}
          </p>
        </div>
        <button
          onClick={() => page && window.open(page.image_url, "_blank")}
          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          title="Open in new tab"
        >
          <ExternalLink className="w-4 h-4 text-gray-400" />
        </button>
        <button
          onClick={onClose}
          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          title="Close"
        >
          <X className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      {/* Image area */}
      <div className="flex-1 overflow-hidden relative bg-gray-50">
        {!page ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-gray-400">No pages available</p>
          </div>
        ) : imageError ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <ImageOff className="w-8 h-8 mb-3 opacity-40" />
            <p className="text-sm">Preview not available</p>
            <button
              onClick={() => window.open(page.image_url, "_blank")}
              className="mt-3 text-xs text-purple-600 hover:text-purple-700 font-medium"
            >
              Open directly
            </button>
          </div>
        ) : (
          <>
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-gray-300" />
              </div>
            )}
            <TransformWrapper
              key={currentPage}
              initialScale={1}
              minScale={0.5}
              maxScale={5}
              centerOnInit
              wheel={{ step: 0.1 }}
              doubleClick={{ mode: "zoomIn" }}
            >
              <TransformComponent
                wrapperStyle={{ width: "100%", height: "100%" }}
                contentStyle={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={page.image_url}
                  alt={`${document.file_name} page ${page.page_number}`}
                  className="max-w-full max-h-full object-contain"
                  onLoad={() => setImageLoaded(true)}
                  onError={() => setImageError(true)}
                />
              </TransformComponent>
              <ZoomControls />
            </TransformWrapper>
          </>
        )}
      </div>

      {/* Page navigation */}
      {pages.length > 1 && (
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={goPrev}
            disabled={currentPage === 0}
            className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100"
          >
            <ChevronLeft className="w-4 h-4" />
            Prev
          </button>
          <span className="text-xs text-gray-400">
            {currentPage + 1} / {pages.length}
          </span>
          <button
            onClick={goNext}
            disabled={currentPage === pages.length - 1}
            className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

// Summary tab content
function SummaryTab({ summaryData, loadingSummary }: { summaryData: any; loadingSummary: boolean }) {
  const [selectedDoc, setSelectedDoc] = useState<any>(null);

  if (loadingSummary) {
    return (
      <div className="flex-1 flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-6 h-6 animate-spin text-gray-300" />
          <span className="text-sm text-gray-400">Loading summary...</span>
        </div>
      </div>
    );
  }

  if (!summaryData || summaryData.serverError) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-20">
        <div className={`w-12 h-12 rounded-full ${summaryData?.serverError ? "bg-red-50" : "bg-gray-50"} flex items-center justify-center mb-4`}>
          {summaryData?.serverError ? (
            <AlertCircle className="w-6 h-6 text-red-400" />
          ) : (
            <FileQuestion className="w-6 h-6 text-gray-300" />
          )}
        </div>
        <p className="text-base font-medium text-gray-900 mb-1">
          {summaryData?.serverError ? "Server error" : "No summary available"}
        </p>
        <p className="text-sm text-gray-400 text-center">
          {summaryData?.serverError
            ? "The server encountered an error. Please try again later."
            : "Summary data could not be loaded"}
        </p>
      </div>
    );
  }

  // Adapt to API response shape — extract documents array
  const documents: any[] =
    summaryData.documents || summaryData.files || summaryData.sources || summaryData.data || [];
  const totalDocuments = documents.length;

  // If a document is selected, show the page viewer
  if (selectedDoc) {
    return (
      <DocumentViewer
        document={selectedDoc}
        onClose={() => setSelectedDoc(null)}
      />
    );
  }

  return (
    <div className="p-4 space-y-3">
      {/* Stats line */}
      <div className="flex items-center gap-2 px-1">
        <FileText className="w-4 h-4 text-gray-400" />
        <span className="text-sm text-gray-500">
          {totalDocuments} document{totalDocuments !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Document list */}
      {documents.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-sm text-gray-400">No documents found for this run</p>
        </div>
      ) : (
        <div className="space-y-1">
          {documents.map((doc: any, index: number) => {
            const fileName = doc.file_name || doc.filename || doc.name || `Document ${index + 1}`;
            const pageCount = doc.pages?.length || 0;

            return (
              <button
                key={`${fileName}-${index}`}
                onClick={() => setSelectedDoc({ ...doc, file_name: fileName })}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors text-left"
              >
                <FileIcon fileName={fileName} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{fileName}</p>
                  {pageCount > 0 && (
                    <p className="text-xs text-gray-400">{pageCount} page{pageCount !== 1 ? "s" : ""}</p>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function ReferenceViewer({
  runInfo,
  selectedCell,
  sourceData,
  loading,
  loadingData,
  dataLoaded: _dataLoaded,
  onRefresh,
  onSignOut,
  userEmail,
  summaryData,
  loadingSummary,
}: ReferenceViewerProps) {
  const [activeTab, setActiveTab] = useState<"cell" | "summary">("cell");
  const [showSettings, setShowSettings] = useState(false);

  const hasSource =
    sourceData &&
    !sourceData.notFound &&
    !sourceData.error &&
    sourceData.images &&
    sourceData.images.length > 0;

  const sourceCount = sourceData?.images?.length || 0;

  // Format cell value for inline display
  const cellLabel =
    selectedCell && selectedCell.value !== null && selectedCell.value !== ""
      ? `${selectedCell.cell} · ${String(selectedCell.value)}`
      : selectedCell?.cell || "";

  return (
    <div className="flex flex-col h-screen bg-white font-sans">
      {/* Header */}
      <header className="flex-shrink-0 px-4 py-3 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <h1 className="text-base font-semibold text-gray-900">Rima</h1>
          <div className="relative">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-1.5 rounded-lg hover:bg-gray-50 transition-colors"
              title="Settings"
            >
              <Settings className="w-4 h-4 text-gray-400" />
            </button>
            {/* Settings dropdown */}
            {showSettings && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                {userEmail && (
                  <div className="px-3 py-2 border-b border-gray-100">
                    <p className="text-xs text-gray-400 truncate">{userEmail}</p>
                  </div>
                )}
                {onSignOut && (
                  <button
                    onClick={() => {
                      setShowSettings(false);
                      onSignOut();
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <LogOut className="w-3.5 h-3.5" />
                    Sign out
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Tab Bar */}
      <div className="flex-shrink-0 px-4 py-2 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setActiveTab("cell")}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                activeTab === "cell"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Cell
            </button>
            <button
              onClick={() => setActiveTab("summary")}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                activeTab === "summary"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Summary
            </button>
          </div>
          {/* Inline cell ref when Cell tab is active */}
          {activeTab === "cell" && selectedCell && (
            <span className="text-xs text-gray-400 font-mono truncate">
              {cellLabel}
            </span>
          )}
        </div>
      </div>

      {/* Data Loading Overlay */}
      {loadingData && (
        <div className="absolute inset-0 bg-white/95 z-50 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-6 h-6 text-purple-500 animate-spin" />
            <p className="text-sm text-gray-500">Loading...</p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {activeTab === "summary" ? (
          <SummaryTab summaryData={summaryData} loadingSummary={loadingSummary} />
        ) : (
          <>
            {/* Loading State */}
            {loading && (
              <div className="flex-1 flex items-center justify-center py-20">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-300" />
                  <span className="text-sm text-gray-400">Finding sources...</span>
                </div>
              </div>
            )}

            {/* No Cell Selected */}
            {!loading && !selectedCell && (
              <div className="flex-1 flex flex-col items-center justify-center px-6 py-20">
                <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mb-4">
                  <MousePointer2 className="w-6 h-6 text-gray-300" />
                </div>
                <p className="text-base font-medium text-gray-900 mb-1">Select a cell</p>
                <p className="text-sm text-gray-400 text-center">
                  Click on any cell to view its source references
                </p>
              </div>
            )}

            {/* No Source Found */}
            {!loading && selectedCell && sourceData?.notFound && (
              <div className="flex-1 flex flex-col items-center justify-center px-6 py-20">
                <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mb-4">
                  <FileQuestion className="w-6 h-6 text-gray-300" />
                </div>
                <p className="text-base font-medium text-gray-900 mb-1">No source found</p>
                <p className="text-sm text-gray-400 text-center">
                  This cell doesn't have a mapped reference
                </p>
              </div>
            )}

            {/* Error State */}
            {!loading && selectedCell && sourceData?.error && (
              <div className="flex-1 flex flex-col items-center justify-center px-6 py-20">
                <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-4">
                  <AlertCircle className="w-6 h-6 text-red-400" />
                </div>
                <p className="text-base font-medium text-gray-900 mb-1">Failed to load</p>
                <p className="text-sm text-gray-400 text-center mb-4">
                  Something went wrong
                </p>
                <button
                  onClick={onRefresh}
                  className="text-sm font-medium text-purple-600 hover:text-purple-700"
                >
                  Try again
                </button>
              </div>
            )}

            {/* Source Cards */}
            {!loading && hasSource && (
              <div className="p-4 space-y-3">
                {sourceData.images!.map((image, index) => (
                  <SourceCard
                    key={`${image.file_name}-${image.page_number}-${index}`}
                    image={image}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Chat Panel */}
      <ChatPanel runId={runInfo.runId} />

      {/* Footer */}
      <footer className="flex-shrink-0 py-2 text-center border-t border-gray-50">
        <span className="text-[10px] text-gray-300 font-mono">Rima v1.0</span>
      </footer>
    </div>
  );
}
